import React, { useState, useEffect, useCallback } from 'react';
import API_BASE_URL from '../config';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './Dashboard/Header.js';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import Lottie from 'lottie-react';
import loader from '../Components/css/loader.json';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Pagination,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowUpward,
  ArrowDownward,
  Sort,
  Add,
  Remove,
  PictureAsPdf,
  TableView,
  ArrowBack,
} from '@mui/icons-material';

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const DataTable = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalAmount, setTotalAmount] = useState(0);
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedBill, setExpandedBill] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'partyName', direction: 'ascending' });
  const [anchorEl, setAnchorEl] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 11;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchData = useCallback(async () => {
    const queryParams = new URLSearchParams(location.search);
    const date = queryParams.get('date');
    const compCode = queryParams.get('CompCode');
    const dueDays = queryParams.get('dueDays');
    const type = queryParams.get('type') || 'Receivable';
    const partyCode = queryParams.get('partyCode');
    const rootCode = queryParams.get('rootCode');
    const areaCode = queryParams.get('areaCode');
    const salesmanCode = queryParams.get('salesmanCode');
    const subAgentCode = queryParams.get('subAgentCode');

    if (!date || !compCode || !dueDays) return;

    const endpoint = type === 'Receivable' ? 'outstanding-receivables' : 'outstanding-payables';
    const url = `${API_BASE_URL}/api/${endpoint}?date=${date}&CompCode=${compCode}&dueDays=${dueDays}`;

    setLoading(true);
    setError(null);

    try {
      const userId = localStorage.getItem('userId');
      const accessToken = localStorage.getItem('accessToken');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'UserId': userId || '',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result = await response.json();

      if (Array.isArray(result)) {
        const filteredResult = result.filter(item => {
          const matchesPartyCode = !partyCode || item.field6 === partyCode;
          const matchesAreaCode = !areaCode || item.field21 === areaCode;
          const matchesRootCode = !rootCode || item.field23 === rootCode;
          const matchesSalesmanCode = !salesmanCode || item.field15 === salesmanCode;
          const matchesSubAgentCode = !subAgentCode || item.field15 === subAgentCode;
          const matchesRootSalesman = (!rootCode && !salesmanCode) || (matchesRootCode && matchesSalesmanCode);

          return (
            matchesPartyCode &&
            matchesAreaCode &&
            matchesRootCode &&
            matchesSalesmanCode &&
            matchesSubAgentCode &&
            matchesRootSalesman
          );
        });

        const formattedData = filteredResult.map(item => ({
          ...item,
          field4: formatDate(item.field4),
          field12: formatDate(item.field12),
        }));

        setData(formattedData);
        const total = formattedData.reduce((acc, item) => acc + (parseFloat(item.field11) || 0), 0);
        setTotalAmount(total);
      } else {
        throw new Error('Unexpected data format');
      }
    } catch (error) {
      console.error("Fetch error:", error);
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [location]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggle = (partyName) => {
    setExpandedRows(prev => ({
      ...prev,
      [partyName]: !prev[partyName],
    }));
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleBillToggle = (billNumber) => {
    setExpandedBill(expandedBill === billNumber ? null : billNumber);
  };

  const groupedData = data.reduce((acc, item) => {
    const partyName = item.field5;
    if (!acc[partyName]) {
      acc[partyName] = [];
    }
    acc[partyName].push(item);
    return acc;
  }, {});

  const type = new URLSearchParams(location.search).get('type');
  const heading = type === 'Receivable' ? 'Outstanding Receivables' : 'Outstanding Payables';
  const selectedDate = new URLSearchParams(location.search).get('date') ? formatDate(new URLSearchParams(location.search).get('date')) : 'N/A';

  const sortedData = Object.keys(groupedData).sort((a, b) => {
    const totalA = groupedData[a].reduce((acc, item) => acc + (parseFloat(item.field11) || 0), 0);
    const totalB = groupedData[b].reduce((acc, item) => acc + (parseFloat(item.field11) || 0), 0);

    if (sortConfig.key === 'partyName') {
      return sortConfig.direction === 'ascending' ? a.localeCompare(b) : b.localeCompare(a);
    } else if (sortConfig.key === 'totalAmount') {
      return sortConfig.direction === 'ascending' ? totalA - totalB : totalB - totalA;
    }
    return 0;
  });

  const filteredData = sortedData.filter(partyName => {
    return groupedData[partyName].some(item => {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      return (
        item.field3.toLowerCase().includes(lowerCaseSearchTerm) ||
        item.field4.toLowerCase().includes(lowerCaseSearchTerm) ||
        item.field7.toLowerCase().includes(lowerCaseSearchTerm) ||
        item.field11.toLowerCase().includes(lowerCaseSearchTerm) ||
        partyName.toLowerCase().includes(lowerCaseSearchTerm)
      );
    });
  });

  const criteria = new URLSearchParams(location.search).get('criteria');
  const criteriaText = criteria ? `Criteria: ${criteria}` : '';

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const generatePDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    const companyName = localStorage.getItem('companyName') || "Default Company Name";
    doc.text(companyName, 105, 20, { align: 'center' });
    doc.setFontSize(8);

    let currentY = 30;

    const headers = ["Bill Number", "Bill Date", "Bill Amt", "Receive Amt", "Return Amt", "Balance Amt"];
    const dataHeaderStyles = {
      fillColor: [70, 130, 180],
      textColor: [255, 255, 255],
      fontSize: 8
    };

    for (const partyName of Object.keys(groupedData)) {
      const partyEntries = groupedData[partyName];

      if (currentY + 10 + (partyEntries.length * 5) + 10 > doc.internal.pageSize.height) {
        doc.addPage();
        currentY = 10;
      }

      doc.autoTable({
        head: [[partyName]],
        startY: currentY,
        theme: 'grid',
        headStyles: {
          textColor: [0, 0, 0],
          fontSize: 8
        },
        styles: {
          fillColor: [240, 240, 240],
          textColor: [0, 0, 0],
          fontSize: 8,
          cellPadding: 2
        },
      });

      currentY = doc.lastAutoTable.finalY;

      const pdfData = partyEntries.map(row => [
        row.field3,
        row.field4,
        row.field7,
        row.field10,
        row.field8,
        row.field11,
      ]);

      doc.autoTable({
        head: [headers],
        body: pdfData,
        startY: currentY,
        theme: 'grid',
        headStyles: dataHeaderStyles,
        columnStyles: {
          2: { halign: 'right', cellWidth: 25 },
          3: { halign: 'right', cellWidth: 25 },
          4: { halign: 'right', cellWidth: 25 },
          5: { halign: 'right', cellWidth: 25 },
        },
        styles: {
          fontSize: 8,
          cellPadding: 2,
        }
      });

      currentY = doc.lastAutoTable.finalY + 5;

      const totalBalance = partyEntries.reduce((acc, row) => acc + parseFloat(row.field11 || 0), 0);
      const totalText = `Total Balance: ${totalBalance.toFixed(2)}`;
      doc.setFont('helvetica', 'bold');
      doc.text(totalText, 145, currentY);
      currentY += 10;

      doc.setFont('helvetica', 'normal');

      const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
      doc.text(`Page ${currentPage}`, 105, doc.internal.pageSize.height - 10, { align: 'center' });
    }

    doc.save(`${heading}.pdf`);
    handleMenuClose();
  };

  const generateExcel = () => {
    const exportData = [];

    exportData.push([
      'Party Name', 'Bill Number', 'Bill Date', 'Bill Amt',
      'Receive Amt', 'Return Amt', 'Balance Amt'
    ]);

    let totalBalanceAmt = 0;

    for (const partyName of Object.keys(groupedData)) {
      const partyEntries = groupedData[partyName];

      exportData.push([partyName]);

      let partyBalanceAmt = 0;

      partyEntries.forEach(entry => {
        const billAmt = entry.field7 || 0;
        const receiveAmt = entry.field10 || 0;
        const returnAmt = entry.field8 || 0;
        const balanceAmt = isNaN(entry.field11) ? 0 : parseFloat(entry.field11);

        partyBalanceAmt += balanceAmt;

        exportData.push([
          entry.field3,
          entry.field4,
          billAmt,
          receiveAmt,
          returnAmt,
          balanceAmt.toFixed(2),
        ]);
      });

      exportData.push([
        'Total', '', '', '', '', partyBalanceAmt.toFixed(2)
      ]);

      totalBalanceAmt += partyBalanceAmt;
    }

    exportData.push([
      'Grand Total', '', '', '', '', totalBalanceAmt.toFixed(2)
    ]);

    const ws = XLSX.utils.aoa_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Outstanding Data');

    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let row = range.s.r; row <= range.e.r; row++) {
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cell = ws[XLSX.utils.encode_cell({ r: row, c: col })];
        if (cell && !isNaN(cell.v)) {
          cell.t = 'n';
        }
      }
    }

    XLSX.writeFile(wb, `${heading}.xlsx`);
    handleMenuClose();
  };

  if (loading && data.length === 0) {
    return (
      <Box className="flex flex-col items-center justify-center h-screen bg-gray-50">
        <Lottie animationData={loader} loop={true} autoplay={true} style={{ height: 150, width: 150 }} />
        <Typography variant="h6" className="mt-4 text-gray-600 font-medium">
          Fetching outstanding data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="flex flex-col items-center justify-center h-screen bg-gray-50 p-4">
        <Typography variant="h6" color="error" className="mb-4 text-center">
          Error: {error.message}
        </Typography>
        <Button variant="contained" onClick={fetchData}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box className="bg-gray-50 min-h-screen flex flex-col w-full">
      <Header />

      <Box className="flex-grow w-full p-1 md:p-4">
        <Box className="mb-4 md:mb-6">
          <Box className="flex items-center justify-center relative mb-4 md:mb-6">
            <Box className="text-center">
              <Typography variant="h5" component="h1" className="font-bold text-gray-800 text-lg md:text-3xl tracking-tight">
                {heading}
              </Typography>
              <Typography variant="subtitle2" className="text-gray-600 font-medium mt-0.5 md:mt-1">
                As on Date: {selectedDate}
              </Typography>
              {criteriaText && (
                <Typography variant="caption" className="text-gray-500 mt-0.5 block">
                  {criteriaText}
                </Typography>
              )}
            </Box>
          </Box>

          <Box className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 bg-white p-2 md:p-3 rounded-xl md:rounded-2xl shadow-sm border border-gray-200">
            <TextField
              variant="outlined"
              placeholder="Search..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              className="w-full lg:max-w-md bg-gray-50/50 rounded-lg"
            />

            <Box className="flex flex-row items-center justify-between lg:justify-end gap-4 w-full lg:w-auto">
              <Box className="text-left lg:text-right">
                <Typography variant="caption" className="text-gray-400 uppercase font-bold tracking-wider text-[10px]">
                  Total
                </Typography>
                <Typography variant="subtitle1" className={`font-bold leading-tight ${totalAmount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                  Rs {totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </Typography>
              </Box>

              <Box>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleMenuClick}
                  endIcon={<ArrowDownward />}
                  size="small"
                  className="rounded-lg px-4 py-1.5"
                >
                  Export
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  PaperProps={{
                    className: "mt-1 shadow-xl border border-gray-100 rounded-xl"
                  }}
                >
                  <MenuItem onClick={generatePDF} className="gap-3 py-2 px-4">
                    <PictureAsPdf className="text-red-500" />
                    <Typography variant="body2" className="font-medium">PDF</Typography>
                  </MenuItem>
                  <MenuItem onClick={generateExcel} className="gap-3 py-2 px-4">
                    <TableView className="text-green-600" />
                    <Typography variant="body2" className="font-medium">Excel</Typography>
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
          </Box>
        </Box>

        <Paper elevation={1} className="overflow-hidden w-full rounded-xl border border-gray-300">
          <Box className="overflow-x-auto">
            <Box className="bg-blue-50 p-2 border-b border-gray-300 flex justify-between items-center min-w-[400px] md:min-w-full">
              <Box
                className="flex items-center cursor-pointer text-blue-800 font-bold text-xs md:text-base hover:text-blue-600 transition-colors"
                onClick={() => handleSort('partyName')}
              >
                Party Name
                {sortConfig.key === 'partyName' ? (
                  sortConfig.direction === 'ascending' ? <ArrowUpward fontSize="inherit" className="ml-1" /> : <ArrowDownward fontSize="inherit" className="ml-1" />
                ) : <Sort fontSize="inherit" className="ml-1 opacity-30" />}
              </Box>
              <Box
                className="flex items-center cursor-pointer text-blue-800 font-bold text-xs md:text-base hover:text-blue-600 transition-colors"
                onClick={() => handleSort('totalAmount')}
              >
                Total Amount
                {sortConfig.key === 'totalAmount' ? (
                  sortConfig.direction === 'ascending' ? <ArrowUpward fontSize="inherit" className="ml-1" /> : <ArrowDownward fontSize="inherit" className="ml-1" />
                ) : <Sort fontSize="inherit" className="ml-1 opacity-30" />}
              </Box>
            </Box >

            {
              currentData.length === 0 ? (
                <Box className="p-8 text-center text-gray-400 font-medium">
                  No data available.
                </Box>
              ) : (
                <Box className="min-w-[400px] md:min-w-full">
                  {currentData.map((partyName, index) => {
                    const partyTotal = groupedData[partyName].reduce((acc, item) => acc + (parseFloat(item.field11) || 0), 0);
                    const isExpanded = !!expandedRows[partyName];

                    return (
                      <Box key={index} className="border-b border-gray-300 last:border-b-0">
                        <Box
                          className="p-1.5 flex justify-between items-center cursor-pointer hover:bg-gray-50/50 transition-colors"
                          onClick={() => handleToggle(partyName)}
                        >
                          <Box className="flex items-center gap-2 min-w-0">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggle(partyName); }} className="bg-gray-100 hover:bg-gray-200 p-1">
                              {isExpanded ? <Remove fontSize="inherit" /> : <Add fontSize="inherit" />}
                            </IconButton>
                            <Typography variant="body2" className="font-semibold text-gray-700 truncate">
                              {partyName}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            className={`font-bold whitespace-nowrap ml-2 ${partyTotal < 0 ? 'text-red-500' : 'text-green-600'}`}
                          >
                            {partyTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </Typography>
                        </Box>

                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box className="p-1 bg-gray-50/50">
                            <TableContainer component={Paper} variant="outlined" className="rounded-lg overflow-x-auto border border-gray-300">
                              <Table size="small" aria-label="invoices table" className="min-w-[450px] md:min-w-full">
                                <TableHead className="bg-gray-100">
                                  <TableRow>
                                    <TableCell className="font-bold text-gray-700 border-r border-gray-300 px-1.5 py-2 text-[11px]">Invoice No.</TableCell>
                                    <TableCell className="font-bold text-gray-700 border-r border-gray-300 px-1.5 py-2 text-[11px]">Date</TableCell>
                                    <TableCell align="right" className="font-bold text-gray-700 border-r border-gray-300 px-1.5 py-2 text-[11px]">Inv Amt</TableCell>
                                    <TableCell align="right" className="font-bold text-gray-700 border-r border-gray-300 px-1.5 py-2 text-[11px]">Bal Amt</TableCell>
                                    <TableCell className="font-bold text-gray-700 px-1.5 py-2 text-[11px]">Due</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {groupedData[partyName].map((row, rowIndex) => (
                                    <React.Fragment key={rowIndex}>
                                      <TableRow hover className="transition-colors border-b border-gray-300 last:border-b-0">
                                        <TableCell component="th" scope="row" className="border-r border-gray-300 px-1.5 py-1.5">
                                          <Box className="flex items-center">
                                            <IconButton
                                              size="small"
                                              onClick={() => handleBillToggle(row.field3)}
                                              className="mr-1 text-blue-500 p-0.5"
                                            >
                                              {expandedBill === row.field3 ? <Remove fontSize="inherit" /> : <Add fontSize="inherit" />}
                                            </IconButton>
                                            <Typography variant="caption" className="font-medium text-gray-700 leading-none">{row.field3}</Typography>
                                          </Box>
                                        </TableCell>
                                        <TableCell className="text-gray-600 border-r border-gray-300 px-1.5 py-1.5 text-[11px]">{row.field4}</TableCell>
                                        <TableCell align="right" className={`font-medium border-r border-gray-300 px-1.5 py-1.5 text-[11px] ${parseFloat(row.field7) < 0 ? 'text-red-500' : 'text-gray-700'}`}>
                                          {parseFloat(row.field7).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </TableCell>
                                        <TableCell align="right" className={`font-bold border-r border-gray-300 px-1.5 py-1.5 text-[11px] ${parseFloat(row.field11) < 0 ? 'text-red-500' : 'text-green-600'}`}>
                                          {parseFloat(row.field11).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </TableCell>
                                        <TableCell className="text-gray-600 px-1.5 py-1.5 text-[11px]">{row.field18}</TableCell>
                                      </TableRow>
                                      <TableRow>
                                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                                          <Collapse in={expandedBill === row.field3} timeout="auto" unmountOnExit>
                                            <Box className="my-1.5 mx-1 p-2 bg-white border border-gray-300 rounded-lg shadow-sm max-w-[220px]">
                                              <Box className="flex flex-col gap-1">
                                                <Box className="grid grid-cols-[90px_1fr] items-center border-b border-gray-50 pb-0.5">
                                                  <Typography variant="caption" className="font-bold text-gray-700">Bill Amt</Typography>
                                                  <Typography variant="caption" className={`font-medium ${parseFloat(row.field7) < 0 ? 'text-red-500' : 'text-gray-600'}`}>{row.field7}</Typography>
                                                </Box>
                                                <Box className="grid grid-cols-[90px_1fr] items-center border-b border-gray-50 pb-0.5">
                                                  <Typography variant="caption" className="font-bold text-gray-700">Receive Amt</Typography>
                                                  <Typography variant="caption" className={`font-medium ${parseFloat(row.field10) < 0 ? 'text-red-500' : 'text-gray-600'}`}>{row.field10}</Typography>
                                                </Box>
                                                <Box className="grid grid-cols-[90px_1fr] items-center border-b border-gray-50 pb-0.5">
                                                  <Typography variant="caption" className="font-bold text-gray-700">Return Amt</Typography>
                                                  <Typography variant="caption" className={`font-medium ${parseFloat(row.field8) < 0 ? 'text-red-500' : 'text-gray-600'}`}>{row.field8}</Typography>
                                                </Box>
                                                <Box className="grid grid-cols-[90px_1fr] items-center">
                                                  <Typography variant="caption" className="font-bold text-gray-700">Balance Amt</Typography>
                                                  <Typography variant="caption" className={`font-bold ${parseFloat(row.field11) < 0 ? 'text-red-500' : 'text-green-600'}`}>{row.field11}</Typography>
                                                </Box>
                                              </Box>
                                            </Box>
                                          </Collapse>
                                        </TableCell>
                                      </TableRow>
                                    </React.Fragment>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
              )
            }
          </Box >
        </Paper >

        <Box className="mt-4 flex justify-center">
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            size="small"
            className="bg-white p-1 rounded-lg shadow-sm border border-gray-200"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default DataTable;